/**
 * Ejected from `experimental/shared/states/RunState.tsx` for NEX-18040: a switched-to loop's state
 * screens are plain data statements ("{{loop}} is generating a briefing now.") with no body prose and
 * no "takes a couple of minutes" pitch — neither of which applies to a loop you are not on and cannot
 * run from. `body` is optional here for that reason, and `RunProgress` takes its title/body as
 * explicit overrides rather than always deriving them from `runningTitle`/`runningBody`, so the same
 * stage rail can sit under either copy. Every other variant still imports the shared version, which
 * has no notion of a switched-to loop, and is unaffected.
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { AgenticRun, RunStage } from '@/features/agentic-insights/components/data/types';
import { runStages } from '@/features/agentic-insights/components/data/types';
import type {
  ToneTokens,
  TypeTokens,
} from '@/features/agentic-insights/components/experimental/shared/tokens';
import { PrimaryAction } from '@/features/agentic-insights/components/experimental/shared/ui/controls';

interface RunStateProps {
  type: Pick<TypeTokens, 'claim' | 'body'>;
  measure: string;
  title: string;
  /** Omitted for a switched-to loop's plain state screens — see the module header. */
  body?: string;
  tone?: string;
  action?: { label: string; onClick: () => void; busy?: boolean };
  secondary?: ReactNode;
  testId: string;
}

/**
 * Every state that is not a readable run, in the surface's own language: a line of type and at most
 * one thing to do. No illustrations and no boxes — a state is a sentence about where the run got to,
 * and dressing it up makes "nothing has been read yet" feel like an error.
 */
export const RunState = ({
  type,
  measure,
  title,
  body,
  tone,
  action,
  secondary,
  testId,
}: RunStateProps) => {
  return (
    <div data-testid={testId} className="flex flex-col gap-4 py-16">
      <p className={`${type.claim} ${measure} ${tone ?? ''}`}>{title}</p>
      {body ? <p className={`${type.body} ${measure}`}>{body}</p> : null}
      {action ? (
        <div className="pt-2">
          <PrimaryAction
            label={action.label}
            busy={action.busy}
            onClick={action.onClick}
            testId="agentic-x-state-action"
          />
        </div>
      ) : null}
      {secondary}
    </div>
  );
};

/**
 * How far along the run is, as the pipeline it actually has rather than as a number.
 *
 * The stages are a fixed, ordered sequence, which makes them the one honest progression signal
 * available: a filled segment means the run is past that point. Deliberately not read as "each of
 * these ran" — `repair` only happens when grounding found something to repair, and `deliveryRisk`
 * can be off — so a skipped stage fills as the run moves beyond it, which is a claim about position
 * and not about work done.
 *
 * The active segment restarts its pulse on every turn. That is the whole use for the turn counter:
 * an unbounded iteration index says nothing as a number, but as a tick it shows the agent is still
 * moving inside a stage that can run for minutes.
 */
const StageRail = ({ stage, turn }: { stage: RunStage; turn: number | undefined }) => {
  const reached = runStages.indexOf(stage);

  return (
    <span
      // The stage is named in text beside this; the rail draws the same fact, so it is not announced
      // twice.
      aria-hidden="true"
      data-testid="agentic-x-stage-rail"
      className="flex items-center gap-1 pt-1"
    >
      {runStages.map((candidate, index) => (
        <span
          // Re-keying the active segment on each turn remounts it, which restarts the pulse.
          key={index === reached ? `${candidate}-${turn ?? 0}` : candidate}
          data-state={index < reached ? 'done' : index === reached ? 'active' : 'pending'}
          className={`h-0.5 w-6 rounded-full ${
            index < reached
              ? 'bg-bg-brand'
              : index === reached
                ? 'bg-bg-brand motion-safe:animate-pulse'
                : 'bg-bg-neutral'
          }`}
        />
      ))}
    </span>
  );
};

/**
 * A run in progress.
 *
 * The stage is the run's own, not the progress record's, and the turn is only trusted when the two
 * still agree: only authoring is multi-turn, so once a run has moved on to grounding, a left-over
 * authoring turn would otherwise render as "Checking findings against evidence · turn 47".
 *
 * The turn is never printed. It is the agent loop's iteration counter — no denominator, nothing a
 * reader can act on — and a rising number is not evidence of health anyway: the heartbeat proves
 * that, and `useEffectiveRunStatus` degrades a stalled run on its own. Its one use is the rail's
 * pulse above.
 */
export const RunProgress = ({
  type,
  tone,
  measure,
  run,
  title,
  body,
  extraSecondary,
}: {
  type: Pick<TypeTokens, 'claim' | 'body' | 'meta'>;
  tone: Pick<ToneTokens, 'neutral'>;
  measure: string;
  run: AgenticRun;
  /** The nav loop's own copy, or a switched-to loop's plain one — see the module header. */
  title: string;
  body?: string;
  /** Appended after the stage rail and progress text — a switched-to loop's way back, since this
   * screen otherwise leaves only "Switch to this loop" as an escape route. */
  extraSecondary?: ReactNode;
}) => {
  const { t } = useTranslation();

  const stage = run.stage;
  const progress = stage !== undefined && run.progress?.stage === stage ? run.progress : undefined;

  return (
    <RunState
      type={type}
      measure={measure}
      testId="agentic-x-state-running"
      tone={tone.neutral}
      title={title}
      body={body}
      secondary={
        <span className="flex flex-col gap-2 pt-2">
          {stage ? <StageRail stage={stage} turn={progress?.turn} /> : null}
          <span className={type.meta} data-testid="agentic-x-progress">
            {stage
              ? t(($) => $.loopAgenticInsights.run.stage[stage])
              : t(($) => $.loopAgenticInsights.experimental.states.progressStarting)}
          </span>
          {extraSecondary}
        </span>
      }
    />
  );
};
